import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { vitrineDosPlanos } from '@maestra/core/telas/dinheiro/vitrineDosPlanos';
import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { fetchPlanConfig, fetchSubscriptionStatus, type BillingCycle } from '@maestra/core/store/slices/subscription';

import { resumoDaPendente, textosDaPendente, useAlterarPendente, type EscolhaAnterior } from './assinaturaPendente';

// A /PLANOS, sem tela. Cópia literal de `pages/Subscription/index`: quem já assina (ativa ou em
// atraso) vê o painel; quem tem pagamento pendente vê o que escolheu e as duas saídas (pagar o que
// já foi gerado, ou alterar o plano e a forma de pagamento; ver `assinaturaPendente.ts`); quem não
// assina vê a vitrine Grátis × PRO e, depois do aceite, o checkout, com o ciclo escolhido
// atravessando para lá. Os textos dos benefícios são do núcleo (os mesmos do app nativo).
//
// Depois de alterar a assinatura pendente, a tela abre direto no pagamento com as escolhas de
// antes marcadas (`escolhaAnterior`, no estado da navegação). `?alterar=1` abre a confirmação de
// alterar (o link que vem do desbloqueio do perfil).

export const usePlanos = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const assinatura = useAppSelector((s) => s.subscription);
  const { plan, status, initialized } = assinatura;
  const location = useLocation();
  const [busca, setBusca] = useSearchParams();
  const alterar = useAlterarPendente();
  const escolhaAnterior = (location.state as { escolhaAnterior?: EscolhaAnterior } | null)?.escolhaAnterior ?? null;

  const [view, setView] = useState<'benefits' | 'checkout'>(escolhaAnterior ? 'checkout' : 'benefits');
  const [cycle, setCycle] = useState<BillingCycle>(escolhaAnterior?.ciclo ?? 'MONTHLY');

  useEffect(() => {
    dispatch(fetchPlanConfig());
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  // Voltou de "Alterar plano ou pagamento" com a tela já montada: abre o pagamento com o de antes.
  useEffect(() => {
    if (!escolhaAnterior) return;
    setCycle(escolhaAnterior.ciclo);
    setView('checkout');
  }, [escolhaAnterior]);

  // O link do desbloqueio do perfil: abre a confirmação de alterar, uma vez.
  const pedirAlterar = alterar.pedir;
  useEffect(() => {
    if (busca.get('alterar') !== '1' || status !== 'pending') return;
    pedirAlterar();
    setBusca((b) => { b.delete('alterar'); return b; }, { replace: true });
  }, [busca, status, pedirAlterar, setBusca]);

  // A vitrine (os dois planos e o preço do ciclo) mora no núcleo desde 31/10/2026: o app mostra a mesma.
  const vitrine = vitrineDosPlanos(plan, cycle, setCycle);

  const estado: 'carregando' | 'painel' | 'pendente' | 'vitrine' | 'checkout' = !initialized ? 'carregando'
    : status === 'active' || status === 'overdue' ? 'painel'
      : status === 'pending' ? 'pendente'
        : view === 'checkout' ? 'checkout' : 'vitrine';

  return {
    estado,
    pendente: {
      ...textosDaPendente(assinatura.billingType),
      resumo: resumoDaPendente(assinatura),
      retomar: () => navigate('/pagamento'),
      alterar,
    },
    vitrine: {
      titulo: vitrine.titulo,
      apoio: vitrine.apoio,
      gratis: vitrine.gratis,
      pro: vitrine.pro,
      ciclo: vitrine.ciclo,
      irParaPagamento: () => setView('checkout'),
    },
    checkout: {
      cicloInicial: cycle,
      perfisIniciais: escolhaAnterior?.perfisExtras ?? 0,
      gbIniciais: escolhaAnterior?.gbExtras ?? 0,
      voltar: () => setView('benefits'),
      aoAtivar: () => navigate('/planos/sucesso'),
      aoIrParaPagamento: () => navigate('/pagamento'),
      aoJaSerAssinante: () => navigate('/settings'),
    },
  };
};

export type DadosDosPlanos = ReturnType<typeof usePlanos>;
